import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  App,
  Popconfirm,
  Space,
  Card,
  Row,
  Col,
  Typography,
  Switch,
  Tag
} from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import api from '../../../services/api';
import { FollowUpCategory } from '../../../types/followup';
import { useAuth } from '../../../contexts/AuthContext';

const { Title } = Typography;
const { TextArea } = Input;

const FollowUpCategoryList: React.FC = () => {
  const [categoryList, setCategoryList] = useState<FollowUpCategory[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<FollowUpCategory | null>(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const { hasPermission } = useAuth();

  const canCreate = hasPermission('leadFUCategory.create');
  const canUpdate = hasPermission('leadFUCategory.update');
  const canDelete = hasPermission('leadFUCategory.delete');

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('lead-follow-up-categories');
      if (response.data && response.data.status === 'success') {
        setCategoryList(response.data.data);
      } else {
        setCategoryList([]);
        message.error('Gagal memuat data kategori follow-up');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Gagal mengambil data kategori follow-up';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const showModal = (category: FollowUpCategory | null = null) => {
    setEditingCategory(category);
    if (category) {
      form.setFieldsValue({
        name: category.name,
        description: category.description,
      });
    } else {
      form.setFieldsValue({
        name: '',
        description: '',
      });
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingCategory(null);
    form.resetFields();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        name: values.name,
        description: values.description,
      };

      if (editingCategory) {
        await api.put(`lead-follow-up-categories/${editingCategory.id}`, payload);
        message.success('Kategori Follow Up berhasil diperbarui');
      } else {
        await api.post('lead-follow-up-categories', payload);
        message.success('Kategori Follow Up berhasil ditambahkan');
      }

      handleCancel();
      fetchCategories();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Terjadi kesalahan. Silakan coba lagi.';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (record: FollowUpCategory) => {
    try {
      setLoading(true);
      const actionVerb = record.is_active ? 'deactivate' : 'activate';
      const endpoint = record.is_active
        ? `/lead-follow-up-categories/${record.id}`
        : `/lead-follow-up-categories/${record.id}/reactivate`;

      if (record.is_active) {
        await api.delete(endpoint);
      } else {
        await api.put(endpoint);
      }

      message.success(`Kategori Follow Up berhasil di-${actionVerb}`);
      fetchCategories();
    } catch (error: any) {
      const actionVerb = record.is_active ? 'deactivate' : 'activate';
      message.error(`Gagal ${actionVerb} kategori Follow Up`);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Nama Kategori',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Deskripsi',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (text: boolean) => (
        <Tag color={text ? 'green' : 'red'}>
          {text ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Aksi',
      key: 'actions',
      width: 150,
      render: (_: any, record: FollowUpCategory) => (
        <Space size="middle">
          {canUpdate && (
            <Button
              icon={<EditOutlined />}
              onClick={() => showModal(record)}
              disabled={!record.is_active}
            />
          )}
          {canDelete && (
            <Popconfirm
              title={`Apakah Anda yakin ingin ${
                record.is_active ? 'deactivate' : 'reactivate'
              } action ini?`}
              onConfirm={() => handleToggleActive(record)}
              okText="Ya"
              cancelText="Tidak"
            >
              <Switch
                checked={record.is_active}
                checkedChildren="Active"
                unCheckedChildren="Inactive"
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            Follow Up Category Management
          </Title>
        </Col>
        {canCreate && (
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => showModal()}
            >
              Tambah Kategori
            </Button>
          </Col>
        )}
      </Row>

      <Card bordered={false}>
        <Table
          columns={columns}
          dataSource={categoryList}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingCategory ? 'Edit Kategori' : 'Tambah Kategori Baru'}
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        confirmLoading={loading}
        destroyOnClose
      >
        <Form form={form} layout="vertical" name="followUpCategoryForm">
          <Form.Item
            name="name"
            label="Nama Kategori"
            rules={[{ required: true, message: 'Nama kategori tidak boleh kosong!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="description"
            label="Deskripsi"
          >
            <TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FollowUpCategoryList;